/**
 * CallIsland — Apple Dynamic-Island-style transformation of the
 * .channel-header-left capsule when a huddle is active in the current
 * channel.
 *
 *   state = 'in-call'   — current user is in the call. Filled moss pill
 *                         with pulsing phone icon, channel name, timer,
 *                         mute + leave micro-buttons. Click anywhere
 *                         else expands to fullscreen huddle view.
 *
 *   state = 'joinable'  — a huddle is active but user has not joined.
 *                         Outlined moss pill with phone icon and
 *                         "Join #channel · N people". Click to join.
 *
 * Exposes `useCallIslandState(channel)` so the parent can set a
 * matching class on the wrapper .channel-header-left for CSS morph.
 */
import { useEffect, useMemo, useState } from 'react'
import { Tip } from '@ofself/shell'
import { useChat } from '../contexts/ChatContext'

export function useCallIslandState(channel) {
  const { joinedHuddle, activeHuddles } = useChat()
  return useMemo(() => {
    if (!channel) return { mode: null, huddle: null }
    if (joinedHuddle && joinedHuddle.channel_id === channel.id) {
      return { mode: 'in-call', huddle: joinedHuddle }
    }
    const other = activeHuddles?.[channel.id]?.huddle
    if (other) return { mode: 'joinable', huddle: other }
    return { mode: null, huddle: null }
  }, [channel?.id, joinedHuddle, activeHuddles])
}

function pad2(n) { return String(n).padStart(2, '0') }

function PhoneIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.94.37 1.86.72 2.73a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.35-1.35a2 2 0 0 1 2.11-.45c.87.35 1.79.59 2.73.72A2 2 0 0 1 22 16.92z" />
    </svg>
  )
}

function MicIcon({ size = 13, muted = false }) {
  if (muted) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="1" y1="1" x2="23" y2="23" />
        <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
        <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
        <line x1="12" y1="19" x2="12" y2="23" />
      </svg>
    )
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
    </svg>
  )
}

function LeaveIcon({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  )
}

export default function CallIsland({ channel, mode, huddle, currentUser }) {
  const { leaveHuddle, joinHuddleFromNotification, toggleHuddleMute } = useChat()
  const [elapsed, setElapsed] = useState(0)

  // Timer starts when the current user joined — not when the huddle was
  // first created. Persist the local join timestamp in sessionStorage so
  // the timer survives tab switches / remounts but resets between calls.
  useEffect(() => {
    if (mode !== 'in-call' || !huddle?.id) return
    const key = `paradigm:huddle-joined:${huddle.id}`
    let joinedAt = Number(sessionStorage.getItem(key)) || 0
    if (!joinedAt) {
      joinedAt = Date.now()
      try { sessionStorage.setItem(key, String(joinedAt)) } catch {}
    }
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - joinedAt) / 1000)))
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [mode, huddle?.id])

  // Clean up the join timestamp when the user leaves.
  useEffect(() => {
    if (mode === 'in-call' || !huddle?.id) return
    try { sessionStorage.removeItem(`paradigm:huddle-joined:${huddle.id}`) } catch {}
  }, [mode, huddle?.id])

  if (mode === 'in-call') {
    const mm = pad2(Math.floor(elapsed / 60))
    const ss = pad2(elapsed % 60)
    const me = huddle?.participants?.find(p => p.user_id === currentUser?.id)
    const isMuted = !!me?.is_muted
    const label = channel?.is_dm ? 'In call' : `#${channel?.name}`

    const handleExpand = () => {
      window.dispatchEvent(new CustomEvent('paradigm:huddle-set-mode', { detail: 'fullscreen' }))
    }
    const handleMute = (e) => { e.stopPropagation(); toggleHuddleMute() }
    const handleLeave = (e) => { e.stopPropagation(); leaveHuddle() }

    return (
      <Tip label="Open call">
      <div className="call-island call-island-in" onClick={handleExpand} role="button">
        <span className="call-island-icon call-island-icon-pulse" aria-hidden="true">
          <PhoneIcon />
        </span>
        <span className="call-island-label">{label}</span>
        <span className="call-island-timer">{mm}:{ss}</span>
        <span className="call-island-actions">
          <Tip label={isMuted ? 'Unmute' : 'Mute'}>
          <button
            type="button"
            className={`call-island-btn ${isMuted ? 'is-muted' : ''}`}
            onClick={handleMute}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            <MicIcon muted={isMuted} />
          </button>
          </Tip>
          <Tip label="Leave call">
          <button
            type="button"
            className="call-island-btn call-island-leave"
            onClick={handleLeave}
            aria-label="Leave call"
          >
            <LeaveIcon />
          </button>
          </Tip>
        </span>
      </div>
      </Tip>
    )
  }

  if (mode === 'joinable') {
    const count = huddle?.participants?.length || 0
    const handleJoin = () => {
      if (channel?.id) joinHuddleFromNotification?.(channel.id)
    }
    return (
      <Tip label="Join call">
      <div className="call-island call-island-join" onClick={handleJoin} role="button">
        <span className="call-island-icon">
          <PhoneIcon />
        </span>
        <span className="call-island-label">
          {channel?.is_dm ? 'Join call' : `Join #${channel?.name}`}
        </span>
        <span className="call-island-count">{count} {count === 1 ? 'in call' : 'in call'}</span>
      </div>
      </Tip>
    )
  }

  return null
}
